import { useMemo, useRef, useEffect, useState } from 'react';
import { Calendar, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react';
import { TIME_BLOCK_NAMES } from '../../hooks/useGoogleCalendar';
import { getEventColor } from '../../utils/timeBlockColors';
import { calculateBlockCapacity, getFillColor } from '../../utils/timeBlockCapacity';
import { useDrag } from '../../contexts/DragContext';

function formatTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

// Sanitize event summary - remove ALL possible line breaks and extra whitespace
function sanitizeSummary(summary) {
  if (!summary) return '';
  // Remove all types of line breaks: \r, \n, Unicode line/paragraph separators
  return summary
    .replace(/[\r\n\u2028\u2029\u000B\u000C\u0085]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getEventTimes(event) {
  const start = new Date(event.start?.dateTime || event.start);
  const end = new Date(event.end?.dateTime || event.end);
  return { start, end };
}

// Minimum visual duration in minutes (corresponds to 2% min height on 15-hour timeline)
const MIN_VISUAL_DURATION_MINUTES = 20;

function eventsOverlap(event1, event2, startHour = 6, endHour = 21) {
  const { start: start1, end: end1 } = getEventTimes(event1);
  const { start: start2, end: end2 } = getEventTimes(event2);

  // Calculate actual durations
  const duration1 = (end1 - start1) / (1000 * 60);
  const duration2 = (end2 - start2) / (1000 * 60);

  // Use visual duration (accounting for minimum height) for overlap detection
  const visualDuration1 = Math.max(duration1, MIN_VISUAL_DURATION_MINUTES);
  const visualDuration2 = Math.max(duration2, MIN_VISUAL_DURATION_MINUTES);

  // Calculate visual end times
  const visualEnd1 = new Date(start1.getTime() + visualDuration1 * 60 * 1000);
  const visualEnd2 = new Date(start2.getTime() + visualDuration2 * 60 * 1000);

  // Check if events visually overlap
  return start1 < visualEnd2 && start2 < visualEnd1;
}

// Calculate column positions for overlapping events
function calculateEventColumns(events) {
  if (!events || events.length === 0) return [];

  // Sort events by start time, then by duration (longer first)
  const sortedEvents = [...events].sort((a, b) => {
    const { start: startA, end: endA } = getEventTimes(a);
    const { start: startB, end: endB } = getEventTimes(b);
    const startDiff = startA - startB;
    if (startDiff !== 0) return startDiff;
    // If same start, longer events first
    return (endB - startB) - (endA - startA);
  });

  const eventColumns = new Map();

  sortedEvents.forEach(event => {
    // Find all events that overlap with this one and have already been placed
    const overlappingEvents = sortedEvents.filter(other =>
      other !== event &&
      eventColumns.has(other.id) &&
      eventsOverlap(event, other)
    );

    // Find the first available column
    const usedColumns = new Set(overlappingEvents.map(e => eventColumns.get(e.id).column));
    let column = 0;
    while (usedColumns.has(column)) {
      column++;
    }

    // Calculate total columns needed for this overlap group
    const maxColumn = Math.max(column, ...Array.from(usedColumns));
    const totalColumns = maxColumn + 1;

    eventColumns.set(event.id, { column, totalColumns });
  });

  // Second pass: update totalColumns for all events in each overlap group
  sortedEvents.forEach(event => {
    const overlappingEvents = sortedEvents.filter(other =>
      eventColumns.has(other.id) &&
      eventsOverlap(event, other)
    );

    if (overlappingEvents.length > 0) {
      const maxTotalColumns = Math.max(
        ...overlappingEvents.map(e => eventColumns.get(e.id).totalColumns),
        eventColumns.get(event.id).totalColumns
      );

      // Update all overlapping events with the same totalColumns
      overlappingEvents.forEach(e => {
        const current = eventColumns.get(e.id);
        eventColumns.set(e.id, { ...current, totalColumns: maxTotalColumns });
      });
      const current = eventColumns.get(event.id);
      eventColumns.set(event.id, { ...current, totalColumns: maxTotalColumns });
    }
  });

  return eventColumns;
}

function getEventDurationMinutes(event) {
  const { start, end } = getEventTimes(event);
  return (end - start) / (1000 * 60);
}

function getEventPosition(event, startHour, endHour, containerHeight = 600) {
  const { start, end } = getEventTimes(event);

  const totalMinutes = (endHour - startHour) * 60;
  const startMinutes = (start.getHours() - startHour) * 60 + start.getMinutes();
  const endMinutes = (end.getHours() - startHour) * 60 + end.getMinutes();

  const top = Math.max(0, (startMinutes / totalMinutes) * 100);

  // Calculate actual height
  const actualHeight = ((endMinutes - startMinutes) / totalMinutes) * 100;

  // Minimum height of 2% for short events to remain readable
  const minHeight = 2;
  const height = Math.max(minHeight, actualHeight);

  return { top: `${top}%`, height: `${height}%`, isShort: actualHeight < minHeight };
}

function isEventNow(event) {
  if (!event.start?.dateTime) return false;
  const now = new Date();
  const { start, end } = getEventTimes(event);
  return now >= start && now <= end;
}

function getCurrentTimePosition(startHour, endHour) {
  const now = new Date();
  const totalMinutes = (endHour - startHour) * 60;
  const currentMinutes = (now.getHours() - startHour) * 60 + now.getMinutes();
  return Math.max(0, Math.min(100, (currentMinutes / totalMinutes) * 100));
}

export default function DashboardScheduleColumn({
  events = [],
  timeBlocks = [],
  tasks,
  getTasksForBlock,
  onDropTask
}) {
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [dragOverBlockId, setDragOverBlockId] = useState(null);
  const timelineRef = useRef(null);
  const currentTimeRef = useRef(null);
  const { draggedTask } = useDrag();

  // Timeline hours (6am to 9pm)
  const startHour = 6;
  const endHour = 21;
  const hours = Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i);

  // Current time position
  const currentTimePos = getCurrentTimePosition(startHour, endHour);
  const isWithinTimeline = currentTimePos >= 0 && currentTimePos <= 100;

  // Calculate column positions for overlapping events
  const eventColumns = useMemo(() => calculateEventColumns(events), [events]);

  // Scroll to current time on mount
  useEffect(() => {
    if (currentTimeRef.current && timelineRef.current) {
      const containerHeight = timelineRef.current.clientHeight;
      const scrollPosition = (currentTimePos / 100) * timelineRef.current.scrollHeight - containerHeight / 3;
      timelineRef.current.scrollTop = Math.max(0, scrollPosition);
    }
  }, [currentTimePos]);

  // Update current time every minute
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  // Check if an event is a time block (can have tasks)
  const isEventTimeBlock = (event) => {
    return timeBlocks.some(tb => tb.id === event.id);
  };

  // Get tasks for a time block
  const getBlockTasks = (block) => {
    if (!isEventTimeBlock(block)) return [];
    if (getTasksForBlock) {
      return getTasksForBlock(block.id);
    }
    return tasks.filter(t => t.gcalTimeBlockId === block.id);
  };

  // Drag and drop handlers
  const handleDragOver = (e, block) => {
    if (!draggedTask) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverBlockId(block.id);
  };

  const handleDragLeave = (e) => {
    // Only clear if we're leaving the block entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverBlockId(null);
    }
  };

  const handleDrop = (e, block) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    if (taskId && onDropTask) {
      onDropTask(taskId, block);
    }
    setDragOverBlockId(null);
  };

  // Count events
  const eventCount = events.length;

  return (
    <div className="dashboard-column">
      {/* Header */}
      <div className="dashboard-column-header">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-monthly" />
          <h2 className="text-sm font-semibold text-text-primary">Today's Schedule</h2>
        </div>
        <span className="text-xs text-text-tertiary">
          {eventCount} event{eventCount !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Timeline */}
      <div
        ref={timelineRef}
        className="dashboard-column-content relative"
        style={{ minHeight: '400px' }}
      >
        {events.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Calendar size={24} className="text-text-muted mb-2" />
            <p className="text-sm text-text-secondary">No events today</p>
            <p className="text-[10px] text-text-tertiary mt-1">
              Events from Google Calendar appear here
            </p>
          </div>
        ) : (
          <div className="relative h-full" style={{ minHeight: `${(endHour - startHour) * 40}px` }}>
            {/* Hour lines */}
            {hours.map((hour, index) => (
              <div
                key={hour}
                className="absolute left-0 right-0 border-t border-border/30"
                style={{ top: `${(index / (hours.length - 1)) * 100}%` }}
              >
                <span className="absolute -top-2 left-0 text-[9px] text-text-muted w-8">
                  {hour > 12 ? `${hour - 12}pm` : hour === 12 ? '12pm' : `${hour}am`}
                </span>
              </div>
            ))}

            {/* Current time indicator */}
            {isWithinTimeline && (
              <div
                ref={currentTimeRef}
                className="absolute left-8 right-0 z-20 pointer-events-none"
                style={{ top: `${currentTimePos}%` }}
              >
                <div className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <div className="flex-1 h-0.5 bg-red-500" />
                </div>
              </div>
            )}

            {/* All Events */}
            <div className="absolute left-10 right-0 top-0 bottom-0">
              {events.map((event) => {
                const position = getEventPosition(event, startHour, endHour);
                const color = getEventColor(event.summary);
                const isNow = isEventNow(event);
                const isExpanded = expandedEvent === event.id;
                const isBlock = isEventTimeBlock(event);
                const blockTasks = getBlockTasks(event);
                const incompleteTasks = blockTasks.filter(t => !t.completedAt);
                const durationMins = getEventDurationMinutes(event);
                const isShort = durationMins <= 30;
                const isDragOver = dragOverBlockId === event.id;

                // Calculate capacity for timeblocks
                const capacity = isBlock ? calculateBlockCapacity(event, incompleteTasks) : null;

                // Get column position for overlapping events
                const columnInfo = eventColumns.get(event.id) || { column: 0, totalColumns: 1 };
                const { column, totalColumns } = columnInfo;
                const width = totalColumns > 1 ? `${(100 / totalColumns) - 1}%` : 'calc(100% - 4px)';
                const left = totalColumns > 1 ? `${(column / totalColumns) * 100}%` : '0';

                return (
                  <div
                    key={event.id}
                    className={`absolute rounded-lg overflow-hidden transition-all
                      ${isNow ? 'ring-2 ring-red-500 ring-offset-1' : ''}
                      ${isExpanded ? 'z-10' : 'hover:z-10'}
                      ${isBlock && blockTasks.length > 0 ? 'cursor-pointer' : ''}
                      ${isDragOver ? 'ring-2 ring-accent ring-inset' : ''}`}
                    style={{
                      top: position.top,
                      minHeight: position.height,
                      left: left,
                      width: width,
                      backgroundColor: isDragOver ? `${color}40` : `${color}20`,
                      borderLeft: `3px solid ${color}`,
                    }}
                    onClick={() => {
                      if (isBlock && blockTasks.length > 0) {
                        setExpandedEvent(isExpanded ? null : event.id);
                      }
                    }}
                    onDragOver={isBlock ? (e) => handleDragOver(e, event) : undefined}
                    onDragLeave={isBlock ? handleDragLeave : undefined}
                    onDrop={isBlock ? (e) => handleDrop(e, event) : undefined}
                    title={`${sanitizeSummary(event.summary)} - ${formatTime(event.start?.dateTime || event.start)}${capacity ? ` (${capacity.scheduledMinutes}/${capacity.totalMinutes} min)` : ''}`}
                  >
                    <div className="px-1 py-0.5 overflow-hidden">
                      {/* Always use compact single-line view for dashboard - hover for full details */}
                      <p className="text-[8px] font-medium text-text-primary truncate whitespace-nowrap overflow-hidden" style={{ lineHeight: '1.4' }}>
                        {sanitizeSummary(event.summary)}
                      </p>
                    </div>

                    {/* Capacity bar for timeblocks */}
                    {isBlock && capacity && capacity.totalMinutes > 0 && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/10 overflow-hidden">
                        <div
                          className="h-full transition-all duration-300"
                          style={{
                            width: `${Math.min(capacity.fillPercentage, 100)}%`,
                            backgroundColor: getFillColor(capacity.fillPercentage, capacity.isOverbooked)
                          }}
                        />
                      </div>
                    )}

                    {/* Drop indicator overlay */}
                    {isDragOver && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <span className="text-[9px] font-medium text-accent bg-white/90 px-1.5 py-0.5 rounded shadow-sm">
                          Drop here
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
